export const THEMES = [
  { id: 'sakaysafe-light', label: 'KilalaRide Light', hint: 'Clean & bright (default)', swatch: ['#F6FBFA', '#0F766E', '#14B8A6'] },
  { id: 'sakaysafe-dark', label: 'KilalaRide Dark', hint: 'Easy on the eyes at night', swatch: ['#071A18', '#14B8A6', '#2DD4BF'] },
  { id: 'ocean', label: 'Ocean', hint: 'Calm blue', swatch: ['#F3FAFF', '#0369A1', '#0EA5E9'] },
  { id: 'ocean-dark', label: 'Ocean Dark', hint: 'Calm blue at night', swatch: ['#06131C', '#38BDF8', '#7DD3FC'] },
  { id: 'sunset', label: 'Sunset', hint: 'Warm & friendly', swatch: ['#FFF7F0', '#EA580C', '#FB923C'] },
  { id: 'sunset-dark', label: 'Sunset Dark', hint: 'Warm glow at night', swatch: ['#1B0F09', '#FB923C', '#FDBA74'] },
  { id: 'lavender', label: 'Lavender', hint: 'Soft & personal', swatch: ['#FBFAFF', '#6D28D9', '#A78BFA'] },
  { id: 'lavender-dark', label: 'Lavender Dark', hint: 'Soft purple at night', swatch: ['#100C1C', '#A78BFA', '#C4B5FD'] },
  { id: 'high-contrast', label: 'High Contrast', hint: 'Easier to read', swatch: ['#FFFFFF', '#0A0A0A', '#0F766E'] },
];

// Light <-> Dark family pairing. High Contrast is a standalone accessibility theme.
export const DARK_OF = {
  'sakaysafe-light': 'sakaysafe-dark',
  ocean: 'ocean-dark',
  sunset: 'sunset-dark',
  lavender: 'lavender-dark',
  'high-contrast': 'sakaysafe-dark',
};
const LIGHT_OF = Object.fromEntries(Object.entries(DARK_OF).map(([l, d]) => [d, l]));

export function isDarkTheme(id) {
  return id === 'sakaysafe-dark' || String(id).endsWith('-dark');
}

// Toggle between a theme and its dark counterpart, staying within the same family.
export function toggleDark(current) {
  if (current === 'system') return 'sakaysafe-dark';
  if (isDarkTheme(current)) return LIGHT_OF[current] || 'sakaysafe-light';
  return DARK_OF[current] || 'sakaysafe-dark';
}

// Optional "sterile -> modern tech" spectrum labels mapped to the same themes.
export const SPECTRUM = [
  { name: 'Clinic', theme: 'high-contrast', vibe: 'Sterile, maximum clarity' },
  { name: 'Paper', theme: 'sakaysafe-light', vibe: 'Clean white, friendly' },
  { name: 'Coast', theme: 'ocean', vibe: 'Calm ocean blues' },
  { name: 'Warm Welcome', theme: 'sunset', vibe: 'Warm & approachable' },
  { name: 'Signal', theme: 'sakaysafe-dark', vibe: 'Modern tech, crisp teal glow' },
];

import { create } from 'zustand';

const THEME_KEY = 'sakaysafe-theme';
const VALID = new Set(THEMES.map((t) => t.id).concat('system'));

function resolveSystem() {
  const dark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return dark ? 'sakaysafe-dark' : 'sakaysafe-light';
}

export function getStoredTheme() {
  const t = localStorage.getItem(THEME_KEY);
  return VALID.has(t) ? t : 'sakaysafe-light';
}

export function applyTheme(theme) {
  const chosen = VALID.has(theme) ? theme : 'sakaysafe-light';
  const effective = chosen === 'system' ? resolveSystem() : chosen;
  document.documentElement.setAttribute('data-theme', effective);
  document.documentElement.classList.toggle('dark', effective === 'sakaysafe-dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const bg = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
    meta.setAttribute('content', bg || '#0F766E');
  }
}

export function setTheme(theme) {
  localStorage.setItem(THEME_KEY, theme);
  applyTheme(theme);
  try { useThemeStore.getState().setThemeState(theme); } catch (e) { console.debug('theme store update skipped:', e); }
}

// Shared reactive theme state so every ThemeSwitcher instance stays in sync.
export const useThemeStore = create((set) => ({
  theme: getStoredTheme(),
  setThemeState: (t) => set({ theme: t }),
}));

export function initTheme() {
  const stored = localStorage.getItem(THEME_KEY) || 'sakaysafe-light';
  applyTheme(stored);
  if (window.matchMedia) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if ((localStorage.getItem(THEME_KEY) || 'sakaysafe-light') === 'system') applyTheme('system');
    });
  }
}
